import * as React from 'react';
import { useIntl } from 'react-intl';
import Success from 'blocks/layouts/EmailCapture/assets/success.inline.svg';
import classnames from 'classnames';
import useNewsletterSignup from '../../hooks/useNewsletterSignup';
import Loading from '../Loading';
import * as styles from './NewsletterSignUp.module.scss';

export type NewsletterSignupProps = {
    inputId?: string;
    list?: string | null;
    // input component
    Component: React.ElementType;
};

export default function NewsletterSignup({
    inputId = 'mail-signup',
    list = null,
    Component,
}: NewsletterSignupProps) {
    const intl = useIntl();

    const {
        handleSubmit,
        email: userMail,
        handleChangeEmail,
        status,
    } = useNewsletterSignup(list);
    const statusRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        // Focus only when a meaningful status appears
        if (
            statusRef.current &&
            ['success', 'invalidEmail', 'failed'].includes(status)
        ) {
            statusRef.current.focus();
        }
    }, [status]);

    return (
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {status !== 'success' && status !== 'submitting' && (
                <Component
                    className={classnames(styles.emailInput, {
                        [styles.emailInputFieldError]:
                            status === 'invalidEmail',
                    })}
                    name={inputId}
                    id={inputId}
                    type="email"
                    placeholder="Your email address"
                    value={userMail}
                    onChange={handleChangeEmail}
                />
            )}
            {status === 'submitting' && (
                <div
                    className={styles.status}
                    ref={statusRef}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    tabIndex={-1}
                >
                    <Loading
                        size={'30px'}
                        classes={{ root: styles.loadingRoot }}
                    />
                    <span className={styles.loadingText}>
                        {intl.formatMessage({
                            id: 'emailCapture.submitting',
                        })}
                    </span>
                </div>
            )}
            {status === 'success' && (
                <div
                    className={styles.status}
                    ref={statusRef}
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    tabIndex={-1}
                >
                    <Success className={styles.successIcon} />
                    <span className={styles.loadingText}>
                        {intl.formatMessage({
                            id: 'emailCapture.success',
                        })}
                    </span>
                </div>
            )}
            <div
                className={styles.notice}
                ref={statusRef}
                role="status"
                aria-live="polite"
                aria-atomic="true"
                tabIndex={-1}
            >
                <p className={styles.errorText}>
                    {status === 'invalidEmail' &&
                        intl.formatMessage({
                            id: 'emailCapture.emailError',
                        })}
                    {status === 'failed' &&
                        intl.formatMessage({
                            id: 'emailCapture.internalError',
                        })}
                </p>
            </div>
        </form>
    );
}
